import { flexRender, type RowData } from "@tanstack/react-table";

import { getHeaderClassName, type IRydeHeader } from "@ryde/shared";

import { TableHeaderCellStyled } from "../cell-templates";
import { ColumnResizeHandle, type IColumnsSizingStore } from "../column-resize";
import { useGridTableHeadCell } from "./use-grid-table-head-cell";

interface IGridRowHeadCellProps<T extends RowData> {
  header: IRydeHeader<T, unknown>;
  isTableSized: boolean;
  /** Omitted for a grid whose columns are not resizable. */
  columnsSizingStore?: IColumnsSizingStore;
}

export const GridTableHeadCell = <T extends RowData>({
  header,
  isTableSized,
  columnsSizingStore,
}: IGridRowHeadCellProps<T>): JSX.Element => {
  const { columnId, columnLabel, columnWidth, isResizable } =
    useGridTableHeadCell({
      header,
      isTableSized,
      isColumnResizeEnabled: Boolean(columnsSizingStore),
    });

  return (
    <TableHeaderCellStyled
      key={header.id}
      colSpan={header.colSpan}
      className={getHeaderClassName(header)}
      data-column-id={columnId}
      $columnWidth={columnWidth}
    >
      {header.isPlaceholder ? null : (
        <div className="th_content">
          {flexRender(header.column.columnDef.header, header.getContext())}
        </div>
      )}
      {isResizable && columnsSizingStore && (
        <ColumnResizeHandle
          columnId={columnId}
          columnLabel={columnLabel}
          columnWidth={columnWidth}
          columnsSizingStore={columnsSizingStore}
        />
      )}
    </TableHeaderCellStyled>
  );
};
